import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const STATUS_BADGES = {
  claiming: { className: 'bg-danger', label: 'Onderbroken' },
  pending: { className: 'bg-warning text-dark', label: 'Niet verstuurd' },
  done: { className: 'bg-success', label: 'Geboekt' },
  partial: { className: 'bg-danger', label: 'Half geboekt' },
  failed: { className: 'bg-danger', label: 'Mislukt' },
  skipped: { className: 'bg-secondary', label: 'Overgeslagen (test)' }
};

const LEG_LABELS = {
  done: 'geboekt',
  failed: 'mislukt',
  pending: 'niet geboekt',
  skipped: 'overgeslagen'
};

const formatDate = (value) => (value ? new Date(value).toLocaleString('nl-NL', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '');

const Leg = ({ leg, sign }) => {
  if (!leg) return null;
  const done = leg.status === 'done';
  return (
    <div className={done ? '' : 'text-danger'}>
      <code>{leg.productCode}</code> {sign}{Math.abs(leg.change)}{' '}
      <small className={done ? 'text-muted' : ''}>
        ({LEG_LABELS[leg.status] || leg.status}{done && leg.stockAfter != null ? `, voorraad nu ${leg.stockAfter}` : ''})
      </small>
    </div>
  );
};

const StockCorrections = () => {
  const [corrections, setCorrections] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const fetchCorrections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filter === 'open' ? { open: 'true' } : {};
      const response = await api.get('/stock-corrections', { params });
      setCorrections(response.data.corrections || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Voorraadcorrecties konden niet worden geladen');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchCorrections();
  }, [fetchCorrections]);

  const runAction = async (id, action) => {
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/stock-corrections/${id}/${action}`);
      await fetchCorrections();
    } catch (err) {
      setError(err.response?.data?.error || 'Actie mislukt');
    } finally {
      setBusyId(null);
    }
  };

  // The server refuses a correction that is still being booked. An interrupted ("claiming")
  // correction can only be checked in Picqer by hand: it may already have been booked.
  // Skipped ones (test server) can be booked again, but do not count as open.
  const canRetry = (c) => ['pending', 'failed', 'partial', 'skipped'].includes(c.status) && !c.acknowledgedAt;
  const canAcknowledge = (c) => ['claiming', 'pending', 'failed', 'partial'].includes(c.status) && !c.acknowledgedAt;

  return (
    <div className="stock-corrections-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Voorraadcorrecties</h1>
          <p className="text-muted">
            Materiaal dat van de parent-rol is gesneden. Bij het afronden van de snijlijst wordt het materiaal
            in Picqer afgeboekt en de parent-rol bijgeboekt.
          </p>
        </div>
        <div className="btn-group">
          <button
            type="button"
            className={`btn ${filter === 'open' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('open')}
          >
            Open
          </button>
          <button
            type="button"
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('all')}
          >
            Alles
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
            </div>
          ) : corrections.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-check-circle fs-1 d-block mb-2"></i>
              {filter === 'open'
                ? 'Geen openstaande voorraadcorrecties.'
                : 'Nog geen voorraadcorrecties. Ze verschijnen hier zodra een snijlijst met materiaal van een parent-rol is afgerond.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Datum</th>
                    <th>Snijlijst</th>
                    <th>Boekingen in Picqer</th>
                    <th>Orders</th>
                    <th>Status</th>
                    <th className="text-end">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {corrections.map((c) => {
                    const badge = STATUS_BADGES[c.status] || { className: 'bg-secondary', label: c.status };
                    const legError = c.child?.status === 'failed' ? c.child.error : c.parent?.error;
                    return (
                      <tr key={c._id}>
                        <td className="text-nowrap">{formatDate(c.createdAt)}</td>
                        <td>
                          <Link to={`/cutlists/${c.cutList}`}>{c.cutListId || 'Snijlijst'}</Link>
                        </td>
                        <td>
                          <Leg leg={c.child} sign="−" />
                          <Leg leg={c.parent} sign="+" />
                          {['failed', 'partial'].includes(c.status) && legError && (
                            <small className="text-danger d-block mt-1">{legError}</small>
                          )}
                          {c.status === 'claiming' && (
                            <small className="text-danger d-block mt-1">
                              Onderbroken tijdens het boeken. Controleer de voorraad in Picqer en corrigeer zo nodig met de hand.
                            </small>
                          )}
                        </td>
                        <td><small>{(c.orderNumbers || []).join(', ')}</small></td>
                        <td>
                          <span className={`badge ${badge.className}`}>{badge.label}</span>
                          {c.acknowledgedAt && (
                            <small className="text-muted d-block mt-1">
                              Handmatig afgehandeld door {c.acknowledgedBy || 'onbekend'}, {formatDate(c.acknowledgedAt)}
                            </small>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          {canRetry(c) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-2"
                              disabled={busyId === c._id}
                              onClick={() => runAction(c._id, 'retry')}
                              title="Stuurt de boekingen die nog niet gelukt zijn opnieuw naar Picqer"
                            >
                              <i className="bi bi-arrow-repeat me-1"></i>
                              Opnieuw boeken
                            </button>
                          )}
                          {canAcknowledge(c) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={busyId === c._id}
                              onClick={() => runAction(c._id, 'acknowledge')}
                              title="Je hebt de voorraad zelf in Picqer gecorrigeerd; de melding verdwijnt"
                            >
                              <i className="bi bi-check2 me-1"></i>
                              Handmatig gedaan
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockCorrections;
